import PageTitle from '../PageTitle';
import Button from '../button/Button';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import BankDetailsForm from './BankDetailsForm';

import type { BankDetailErrors } from './useBankDetailsForm';
import type { TFunction } from 'i18next';
import type { SelectOption } from '../selectInput/SelectInput';
import { AccountType } from 'Constants/Common';
import BankDetailsInfo from 'components/payment/BankDetailsInfo';

interface BankDetails {
    accountHolderName: string;
    sortCode: { key: string; value: string }[];
    accountNumber: string;
    paymentDay: string;
}

interface BankDetailsCommonProps {
    title: string;
    instruction: string;
    existingBankDetails?: BankDetails;
    bankDetails: BankDetails;
    bankDetailErrors: BankDetailErrors;
    daysOptions: SelectOption[];
    t: TFunction;
    onChange: (e: React.ChangeEvent<HTMLInputElement>, key: string) => void;
    onSortCodeChange: (sortCode: { key: string; value: string }[]) => void;
    onPaymentDayChange: (paymentDay: string) => void;
    onSubmit: () => void;
    onYesButtonClick?: () => void;
    onNoButtonClick?: () => void;
    onBack: () => void;
    getErrors: () => string[];
    showPaymentDay?: boolean;
    showBankInputSection?: boolean;
    accountType?: string;
    children?: React.ReactNode;
}

const BankDetailsCommon = ({
    title,
    instruction,
    existingBankDetails,
    bankDetails,
    bankDetailErrors,
    daysOptions,
    t,
    onChange,
    onSortCodeChange,
    onPaymentDayChange,
    onSubmit,
    onYesButtonClick,
    onNoButtonClick,
    onBack,
    getErrors,
    showPaymentDay = false,
    showBankInputSection,
    accountType,
    children
}: BankDetailsCommonProps) => (
    <div className='card page-content'>
        <PageTitle title={title} />

        <p className='p-3 mb-0'>{instruction}</p>
        {accountType === AccountType.Disbursement &&
            existingBankDetails.accountNumber !== '' && (
                <>
                    <p className='p-3 mb-0'>
                        {t('repayment_bank_details_collection')}
                    </p>
                    <BankDetailsInfo
                        accountName={existingBankDetails.accountHolderName}
                        accountNumber={existingBankDetails.accountNumber}
                        sortCode={existingBankDetails.sortCode
                            .map((v) => v.value)
                            .join()}
                        className='m-3'
                        minimized
                    />
                </>
            )}
        {showBankInputSection === true && (
            <BankDetailsForm
                bankDetails={bankDetails}
                bankDetailErrors={bankDetailErrors}
                daysOptions={daysOptions}
                t={t}
                onChange={onChange}
                onSortCodeChange={onSortCodeChange}
                onPaymentDayChange={onPaymentDayChange}
                showPaymentDay={showPaymentDay}
            />
        )}
        {children}

        <div className='card-footer page-content-footer'>
            <ErrorSummary
                title={t('page_errors')}
                errors={getErrors()}
                className='card-error'
            />
        </div>
        {accountType === AccountType.Disbursement && !showBankInputSection && (
            <div className='bottom-navigation'>
                <Button
                    text='Yes'
                    className='btn btn-primary progress-button'
                    onClick={onYesButtonClick}
                />
                <Button
                    text='No'
                    className='btn btn-secondary progress-button'
                    onClick={onNoButtonClick}
                />
                <Button
                    text={t('back')}
                    className='btn btn-secondary progress-button'
                    onClick={onBack}
                />
            </div>
        )}
        {showBankInputSection && (
            <div className='bottom-navigation'>
                <Button
                    text={t('submit')}
                    className='btn btn-primary progress-button'
                    onClick={onSubmit}
                />
                <Button
                    text={t('back')}
                    className='btn btn-secondary progress-button'
                    onClick={onBack}
                />
            </div>
        )}
    </div>
);

export default BankDetailsCommon;
